import type { HeroArchetype } from '../data/heroes.ts';
import { heroPortrait } from './portraits.ts';

export interface HeroWheelAbility {
  id: string;
  name: string;
  glyph: string;
  key: string;
  rules: string;
  cooldown: number;
  cooldownMax: number;
  locked: boolean;
  lockLabel: string;
  disabledReason: string;
  armed: boolean;
}

/** Display state only. The HUD supplies every simulation decision. */
export interface HeroWheelModel {
  identity: string;
  archetype: HeroArchetype;
  name: string;
  className: string;
  level: number;
  /** Current health as a fraction between zero and one. */
  hp: number;
  status: string;
  abilities: readonly HeroWheelAbility[];
  points?: number;
}

export interface HeroWheelCallbacks {
  onAbility(id: string): void;
  onFocus(): void;
  onSheet(): void;
}

/** Kept here so the component can also be mounted outside the full game HUD. */
export const HERO_WHEEL_CSS = `
.hero-wheel {
  --hw-bronze: #b39158; --hw-light: #f1dba4; --hw-bone: #f4eddd;
  position: relative; isolation: isolate; width: 270px; height: 290px;
  flex: 0 0 auto; color: var(--hw-bone); pointer-events: auto;
  font: 12px/1.35 'Inter', system-ui, sans-serif;
  filter: drop-shadow(0 5px 12px #0009);
}
.hero-wheel *, .hero-wheel *::before, .hero-wheel *::after { box-sizing: border-box; }
.hero-wheel button {
  appearance: none; margin: 0; padding: 0; border: 0; font: inherit;
  color: inherit; text-transform: none; letter-spacing: normal; cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.hero-wheel button:focus-visible { outline: 2px solid #ffedb8; outline-offset: 5px; }
.hw-stage { position: absolute; inset: 0 0 auto; height: 248px; }
.hw-orbit {
  position: absolute; width: 206px; height: 206px; left: 32px; top: 12px;
  border-radius: 50%; border: 1px solid #c5a1687d;
  background: radial-gradient(circle, #151b1dea 0 40%, #101719d9 51%, #343027c7 69%, #111918b0 70%);
  box-shadow: inset 0 0 0 5px #151b1bc2, inset 0 0 0 6px #aa895338, 0 0 0 4px #080d1059;
  pointer-events: none;
}
.hw-orbit::before {
  content: ''; position: absolute; inset: 14px; border: 1px dashed #b7975b47; border-radius: 50%;
}
.hw-orbit::after {
  content: ''; position: absolute; inset: 31px; border: 1px solid #b5985a4a; border-radius: 50%;
  box-shadow: 0 1px 0 #ffe7b217;
}
.hero-wheel .hw-focus {
  position: absolute; left: 135px; top: 119px; width: 104px; height: 104px;
  transform: translate(-50%, -50%); border-radius: 50%; background: #141b1b;
  box-shadow: 0 3px 10px #000b, inset 0 0 0 1px #f0d99a54;
}
.hw-portrait {
  position: absolute; inset: 7px; overflow: hidden; border-radius: 50%;
  background: #252a25; border: 1px solid #bda27485;
}
.hw-portrait svg { position: absolute; left: -1%; top: -4%; width: 102%; height: 122.4%; }
.hw-vital { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; pointer-events: none; }
.hw-vital circle { fill: none; stroke-width: 3; }
.hw-hp-track { stroke: #09110e; }
.hw-hp-fill { stroke: #b7ce8b; stroke-linecap: round; transform: rotate(-90deg); transform-origin: 52px 52px; }
.hero-wheel.low-health .hw-hp-fill { stroke: #e69b84; }
.hero-wheel .hw-focus:hover .hw-portrait { border-color: #f1dba4; filter: brightness(1.13); }
.hw-level {
  position: absolute; bottom: -6px; left: 50%; transform: translateX(-50%);
  min-width: 38px; padding: 2px 7px; color: #f8e7bf; background: linear-gradient(#554735, #282b27);
  border: 1px solid #b59a68; border-radius: 9px; font-size: 10px; font-weight: 700;
  font-variant-numeric: tabular-nums; white-space: nowrap; box-shadow: 0 2px 4px #0008;
}
.hw-status {
  position: absolute; top: 181px; left: 84px; width: 102px; text-align: center;
  color: #d0d4c9; font-size: 10px; line-height: 13px; text-shadow: 0 1px 3px #000;
  overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
}
.hero-wheel .hw-ability {
  position: absolute; width: 56px; height: 56px; transform: translate(-50%, -50%);
  border-radius: 50%; background: none; z-index: 2;
}
.hw-medallion {
  position: absolute; inset: 0; display: grid; place-items: center; border-radius: 50%;
  border: 1px solid #d4b67e; background: linear-gradient(145deg, #6c5c41 0%, #393a30 16%, #1b2424 48%, #121b1e 84%);
  box-shadow: inset 0 0 0 3px #262b25, inset 0 0 0 4px #ac925a6e, 0 3px 5px #0009;
  overflow: hidden;
}
.hw-glyph {
  position: relative; z-index: 1; font: 29px/1 'Segoe UI Symbol', 'Noto Sans Symbols 2', serif;
  font-variant-emoji: text; color: #f2ddb0; text-shadow: 0 2px 2px #000b;
}
.hw-sweep {
  position: absolute; inset: 4px; border-radius: 50%; z-index: 2; pointer-events: none;
  background: conic-gradient(from 0deg, #05090ed9 var(--hw-cooldown, 0%), transparent 0);
}
.hw-cooldown {
  position: absolute; inset: 0; display: grid; place-items: center; z-index: 3;
  font-size: 19px; font-weight: 750; color: #fff6e0; font-variant-numeric: tabular-nums;
  text-shadow: 0 1px 4px #000, 0 1px 2px #000; pointer-events: none;
}
.hw-key {
  position: absolute; left: -3px; top: -3px; min-width: 19px; height: 19px; padding: 1px 4px;
  display: grid; place-items: center; background: #e1cca1; color: #18201f;
  border: 1px solid #f4e2bc; border-radius: 5px 2px 5px 2px; font-size: 11px; font-weight: 800;
  box-shadow: 0 1px 3px #000a; z-index: 4;
}
.hw-ability-name {
  position: absolute; top: 59px; left: -22px; width: 100px; min-height: 14px;
  color: #f2ecdf; font-size: 10px; line-height: 12px; font-weight: 600; text-align: center;
  text-shadow: 0 1px 2px #000, 0 0 4px #000, 0 0 6px #000;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.hw-ability.left .hw-ability-name { left: -26px; width: 78px; }
.hw-ability.right .hw-ability-name { left: 4px; width: 78px; }
.hw-ability.top .hw-ability-name { left: 61px; top: 11px; width: 78px; text-align: left; }
.hw-lock {
  position: absolute; z-index: 4; bottom: -3px; left: 50%; transform: translateX(-50%);
  max-width: 72px; padding: 1px 5px; background: #2f3535; color: #e4ded0;
  border: 1px solid #7b8178; border-radius: 3px; font-size: 9px; line-height: 12px; font-weight: 700;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; box-shadow: 0 2px 3px #0009;
}
.hw-ability.locked .hw-medallion { border-color: #747a73; background: linear-gradient(145deg, #444a43, #202a2a 45%, #172023); }
.hw-ability.locked .hw-glyph { color: #afb7ad; }
.hw-ability.locked .hw-key { background: #6c756c; color: #f1eee5; border-color: #989d8a; }
.hw-ability.locked .hw-ability-name { color: #c7cdc6; }
.hw-ability.unavailable:not(.locked) .hw-medallion { border-color: #827b6b; }
.hw-ability.unavailable:not(.locked) .hw-glyph { color: #b1ada2; }
.hero-wheel .hw-ability:hover .hw-medallion, .hero-wheel .hw-ability:focus-visible .hw-medallion {
  border-color: #fff0c6; box-shadow: inset 0 0 0 3px #4f4b38, inset 0 0 0 4px #e1bc75, 0 3px 5px #0009;
}
.hero-wheel .hw-ability.armed .hw-medallion {
  border-color: #ffe0a0; background: radial-gradient(circle, #80653a, #393724 65%, #181e1c);
  box-shadow: inset 0 0 0 3px #6d5f3b, inset 0 0 0 4px #edca79, 0 0 0 3px #d7b26a4f, 0 0 15px #e4b35b50;
}
.hw-ability.armed .hw-ability-name { color: #ffe7b2; }
.hw-identity {
  position: absolute; left: 16px; right: 16px; bottom: 0; height: 37px; padding: 4px 43px 3px 10px;
  background: linear-gradient(100deg, #151e21e8, #252a25f5 50%, #172022e8);
  border-top: 1px solid #bc9a6073; border-bottom: 1px solid #81704a50;
  box-shadow: inset 0 1px 0 #f9e6ab0a;
}
.hw-name { font-family: 'Cinzel', Georgia, serif; font-size: 12px; font-weight: 650; color: #f0dbab; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hw-class { color: #c3cdc9; font-size: 10px; line-height: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hero-wheel .hw-sheet {
  position: absolute; right: 5px; top: 4px; width: 31px; height: 28px; display: grid; place-items: center;
  border: 1px solid #ac905c80; border-radius: 3px; background: linear-gradient(#444435, #252e2a); color: #eed4a0;
}
.hw-sheet svg { width: 18px; height: 18px; }
.hero-wheel .hw-sheet:hover { border-color: #f0d6a2; background: #54513a; }
.hw-points {
  position: absolute; right: -7px; top: -7px; min-width: 17px; height: 17px; padding: 1px 3px;
  border-radius: 50%; background: #e1bf75; color: #1c2420; font: 800 10px/15px 'Inter', sans-serif;
  box-shadow: 0 1px 3px #000b;
}
.hw-tip {
  position: absolute; bottom: calc(100% + 8px); left: 0; width: 270px; padding: 11px 13px;
  border: 1px solid #bca372; background: #172021fa; box-shadow: 0 7px 20px #000a, inset 0 1px 0 #fbe4b121;
  color: #ede9dd; pointer-events: none; z-index: 20; font-size: 12px; line-height: 1.5;
}
.hw-tip-name { color: #f2d99e; font-size: 13px; font-weight: 700; margin-bottom: 3px; }
.hw-tip-state { border-top: 1px solid #bca37240; margin-top: 7px; padding-top: 6px; color: #c8d7c1; font-size: 11px; }
.hero-wheel [hidden] { display: none !important; }
@media (max-width: 820px) {
  .hero-wheel { width: 220px; height: 230px; }
  .hw-stage { left: -25px; transform: scale(.8); transform-origin: 50% 0; width: 270px; }
  .hw-ability-name { font-size: 12px; line-height: 14px; }
  .hw-status { font-size: 12px; line-height: 14px; }
  .hw-identity { left: 5px; right: 5px; height: 32px; padding-top: 2px; }
  .hw-name { font-size: 11px; }
  .hero-wheel .hw-sheet { top: 2px; height: 27px; }
  .hw-tip { width: 220px; font-size: 11px; }
}
`;

interface AbilityView {
  button: HTMLButtonElement;
  glyph: HTMLElement;
  name: HTMLElement;
  key: HTMLElement;
  sweep: HTMLElement;
  cooldown: HTMLElement;
  lock: HTMLElement;
  model: HeroWheelAbility;
}

const POSITIONS: ReadonlyArray<ReadonlyArray<readonly [number, number]>> = [
  [],
  [[135, 29]],
  [[40, 109], [230, 109]],
  [[135, 29], [230, 165], [40, 165]],
  [[53, 59], [217, 59], [217, 188], [53, 188]],
  [[135, 29], [230, 101], [195, 192], [75, 192], [40, 101]],
];

function text(el: Element, value: string): void {
  if (el.textContent !== value) el.textContent = value;
}

function attr(el: Element, key: string, value: string): void {
  if (el.getAttribute(key) !== value) el.setAttribute(key, value);
}

function fraction(value: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
}

let nextWheel = 0;

/** Persistent controls; updates change their presentation without replacing them. */
export class HeroWheel {
  readonly el: HTMLElement;
  private readonly stage: HTMLElement;
  private readonly focus: HTMLButtonElement;
  private readonly portrait: HTMLElement;
  private readonly hp: SVGCircleElement;
  private readonly level: HTMLElement;
  private readonly status: HTMLElement;
  private readonly name: HTMLElement;
  private readonly className: HTMLElement;
  private readonly sheet: HTMLButtonElement;
  private readonly points: HTMLElement;
  private readonly tip: HTMLElement;
  private readonly tipName: HTMLElement;
  private readonly tipRules: HTMLElement;
  private readonly tipState: HTMLElement;
  private readonly abilities = new Map<string, AbilityView>();
  private readonly uid = `hero-wheel-${++nextWheel}`;
  private identity = '';
  private archetype: HeroArchetype | '' = '';
  private hovered = '';
  private focused = '';

  constructor(private readonly callbacks: HeroWheelCallbacks) {
    if (!document.getElementById('hero-wheel-styles')) {
      const style = document.createElement('style');
      style.id = 'hero-wheel-styles';
      style.textContent = HERO_WHEEL_CSS;
      document.head.appendChild(style);
    }
    this.el = document.createElement('section');
    this.el.className = 'hero-wheel';
    this.el.innerHTML = `
      <div class="hw-stage">
        <div class="hw-orbit" aria-hidden="true"></div>
        <button type="button" class="hw-focus">
          <span class="hw-portrait" aria-hidden="true"></span>
          <svg class="hw-vital" viewBox="0 0 104 104" aria-hidden="true">
            <circle class="hw-hp-track" cx="52" cy="52" r="49"/>
            <circle class="hw-hp-fill" cx="52" cy="52" r="49" pathLength="100"/>
          </svg>
          <span class="hw-level"></span>
        </button>
        <span class="hw-status"></span>
      </div>
      <div class="hw-identity">
        <div class="hw-name"></div><div class="hw-class"></div>
        <button type="button" class="hw-sheet" aria-label="Open character sheet">
          <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round">
            <path d="M12 5C8 2 4 3 2 4v15c4-1 7-1 10 2 3-3 6-3 10-2V4c-2-1-6-2-10 1Zm0 0v16M5 8l4 1M5 12l4 1m6-4 4-1m-4 5 4-1"/>
          </svg><span class="hw-points" hidden></span>
        </button>
      </div>
      <div class="hw-tip" hidden aria-hidden="true">
        <div class="hw-tip-name"></div><div class="hw-tip-rules"></div><div class="hw-tip-state"></div>
      </div>`;
    const find = <T extends Element>(selector: string): T => this.el.querySelector(selector) as T;
    this.stage = find('.hw-stage');
    this.focus = find('.hw-focus');
    this.portrait = find('.hw-portrait');
    this.hp = find('.hw-hp-fill');
    this.level = find('.hw-level');
    this.status = find('.hw-status');
    this.name = find('.hw-name');
    this.className = find('.hw-class');
    this.sheet = find('.hw-sheet');
    this.points = find('.hw-points');
    this.tip = find('.hw-tip');
    this.tipName = find('.hw-tip-name');
    this.tipRules = find('.hw-tip-rules');
    this.tipState = find('.hw-tip-state');
    this.focus.onclick = () => this.callbacks.onFocus();
    this.sheet.onclick = () => this.callbacks.onSheet();
    this.el.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape') {
        this.hovered = '';
        this.focused = '';
        this.updateTip();
      }
    });
  }

  update(model: HeroWheelModel): void {
    if (model.identity !== this.identity || model.archetype !== this.archetype) {
      this.identity = model.identity;
      this.archetype = model.archetype;
      // Portraits also appear on the sheet: namespace their SVG paint servers.
      this.portrait.innerHTML = heroPortrait(model.archetype, { size: 100, frame: false })
        .replaceAll('hp-', `${this.uid}-hp-`);
    }
    attr(this.el, 'aria-label', `${model.name}, ${model.className} hero controls`);
    text(this.name, model.name);
    attr(this.name, 'title', model.name);
    text(this.className, model.className);
    text(this.level, `Lv ${model.level}`);
    text(this.status, model.status);
    const hp = fraction(model.hp);
    attr(this.hp, 'stroke-dasharray', `${(hp * 100).toFixed(2)} 100`);
    this.el.classList.toggle('low-health', hp < .3);
    const focusLabel = `Focus ${model.name}. ${model.className}, level ${model.level}. Health ${Math.round(hp * 100)}%. ${model.status}`;
    attr(this.focus, 'aria-label', focusLabel);
    attr(this.focus, 'title', focusLabel);
    const points = Math.max(0, Math.floor(model.points ?? 0));
    this.points.hidden = points === 0;
    text(this.points, points.toString());
    const sheetLabel = `Open ${model.name}'s character sheet${points > 0 ? `. ${points} unspent skill point${points === 1 ? '' : 's'}` : ''}`;
    attr(this.sheet, 'aria-label', sheetLabel);
    attr(this.sheet, 'title', sheetLabel);

    const slots = model.abilities.slice(0, 5);
    const present = new Set(slots.map((ability) => ability.id));
    for (const [id, view] of this.abilities) {
      if (present.has(id)) continue;
      view.button.remove();
      this.abilities.delete(id);
      if (this.hovered === id) this.hovered = '';
      if (this.focused === id) this.focused = '';
    }
    for (let i = 0; i < slots.length; i++) {
      const ability = slots[i];
      let view = this.abilities.get(ability.id);
      if (!view) {
        view = this.makeAbility(ability);
        this.abilities.set(ability.id, view);
        this.stage.appendChild(view.button);
      }
      view.model = ability;
      const [x, y] = POSITIONS[slots.length][i];
      const { button } = view;
      if (button.style.left !== `${x}px`) button.style.left = `${x}px`;
      if (button.style.top !== `${y}px`) button.style.top = `${y}px`;
      button.classList.toggle('left', x < 100);
      button.classList.toggle('right', x > 170);
      button.classList.toggle('top', x === 135);
      // Upper names sit outside the portrait; edge labels stay inside the wheel.
      const nameLeft = x === 135 ? 61 : y < 130 ? (x < 100 ? 2 : 190) - x + 28 : Math.max(2, Math.min(190, x - 39)) - x + 28;
      if (view.name.style.left !== `${nameLeft}px`) view.name.style.left = `${nameLeft}px`;
      button.classList.toggle('locked', ability.locked);
      button.classList.toggle('armed', ability.armed && !ability.locked);
      const cooling = !ability.locked && ability.cooldown > 0;
      const unavailable = !ability.locked && (cooling || ability.disabledReason !== '');
      button.classList.toggle('unavailable', unavailable);
      // Locked controls remain actionable: they open the existing skill sheet.
      attr(button, 'aria-disabled', String(unavailable));
      attr(button, 'aria-pressed', String(ability.armed && !ability.locked));
      text(view.glyph, ability.glyph);
      text(view.name, ability.name);
      text(view.key, ability.key);
      view.key.hidden = ability.key === '';
      text(view.lock, ability.lockLabel || 'Locked');
      view.lock.hidden = !ability.locked;
      view.cooldown.hidden = !cooling;
      view.sweep.hidden = !cooling;
      text(view.cooldown, cooling ? Math.ceil(ability.cooldown).toString() : '');
      const progress = cooling ? fraction(ability.cooldown / Math.max(ability.cooldownMax, ability.cooldown, .001)) : 0;
      const sweep = `${(progress * 100).toFixed(1)}%`;
      if (view.sweep.style.getPropertyValue('--hw-cooldown') !== sweep) view.sweep.style.setProperty('--hw-cooldown', sweep);
      const state = this.abilityState(ability);
      const label = `${ability.key ? `${ability.key}: ` : ''}${ability.name}. ${ability.rules} ${state}`;
      attr(button, 'aria-label', label);
      attr(button, 'title', `${ability.name}${ability.key ? ` [${ability.key}]` : ''}\n${ability.rules}\n${state}`);
    }
    // Shared powers keep their button when the character changes, but their
    // keyboard traversal order must follow the new character's visible keys.
    let previous: ChildNode = this.status;
    for (const slot of slots) {
      const button = this.abilities.get(slot.id)!.button;
      if (previous.nextSibling !== button) this.stage.insertBefore(button, previous.nextSibling);
      previous = button;
    }
    this.updateTip();
  }

  private makeAbility(model: HeroWheelAbility): AbilityView {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'hw-ability';
    button.dataset.ability = model.id;
    button.innerHTML = `<span class="hw-medallion" aria-hidden="true"><span class="hw-glyph"></span>
      <span class="hw-sweep" hidden></span><span class="hw-cooldown" hidden></span></span>
      <span class="hw-key" aria-hidden="true"></span><span class="hw-lock" aria-hidden="true" hidden></span>
      <span class="hw-ability-name" aria-hidden="true"></span>`;
    const part = (selector: string): HTMLElement => button.querySelector(selector) as HTMLElement;
    const view: AbilityView = {
      button, model, glyph: part('.hw-glyph'), name: part('.hw-ability-name'), key: part('.hw-key'),
      sweep: part('.hw-sweep'), cooldown: part('.hw-cooldown'), lock: part('.hw-lock'),
    };
    button.onclick = () => {
      const current = view.model;
      if (current.locked) this.callbacks.onSheet();
      else if (current.cooldown <= 0 && !current.disabledReason) this.callbacks.onAbility(current.id);
    };
    button.onmouseenter = () => { this.hovered = model.id; this.updateTip(); };
    button.onmouseleave = () => { this.hovered = ''; this.updateTip(); };
    button.onfocus = () => { this.focused = model.id; this.updateTip(); };
    button.onblur = () => { this.focused = ''; this.updateTip(); };
    return view;
  }

  private abilityState(model: HeroWheelAbility): string {
    if (model.locked) return `${model.lockLabel || 'Locked'}. ${model.disabledReason ? `${model.disabledReason} ` : ''}Open the character sheet.`;
    if (model.cooldown > 0) return `Ready in ${Math.ceil(model.cooldown)}s.${model.disabledReason ? ` ${model.disabledReason}` : ''}`;
    if (model.disabledReason) return model.disabledReason;
    if (model.armed) return 'Selected — choose a target. Click again to cancel.';
    return `Ready${model.key ? ` — ${model.key}` : ''}. Select this ability, then choose a target.`;
  }

  private updateTip(): void {
    const view = this.abilities.get(this.hovered || this.focused);
    this.tip.hidden = !view;
    if (!view) return;
    text(this.tipName, `${view.model.name}${view.model.key ? ` · ${view.model.key}` : ''}`);
    text(this.tipRules, view.model.rules);
    text(this.tipState, this.abilityState(view.model));
  }
}
